"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Head from "next/head";
import toast from "react-hot-toast";

import Select from "react-select";
import { ChantBotContact as Icon_ChantBotContact } from "@/components/customer/icons/ChantBotContact";
import { ContactWhatsapp as Icon_ContactWhatsapp } from "@/components/customer/icons/ContactWhatsapp";
import { ReviewStar as Icon_ReviewStar } from "@/components/customer/icons/ReviewStar";
const SvgIcons = { ChantBotContact: Icon_ChantBotContact, ContactWhatsapp: Icon_ContactWhatsapp, ReviewStar: Icon_ReviewStar };
import TestiReviewCommonImages from "./otherpages/TestiReviewCommonImages";
import FaqSection from "./FaqSection";
import { contactFaqs } from "@/app/contact-us/faq";
import contactimg from "@/images/Mainpage/ContactUsImage.webp"

import dynamic from 'next/dynamic';
import type ReCAPTCHABase from 'react-google-recaptcha';
import { getImageUrl, getFlagUrl } from "@/lib/imageUrl";
import { getApiUrl } from "@/lib/apiUrl";
import { stripDangerousChars } from "@/lib/validation";

const ReCAPTCHA = dynamic(() => import('react-google-recaptcha'), {
  ssr: false,
  loading: () => (
    <div className="h-[78px] w-[304px] bg-gray-100 rounded animate-pulse" />
  ),
}) as typeof ReCAPTCHABase;
// Define interfaces
interface CustomOption {
  value: string;
  label: string;
  flag: string;
  calling_code: string;
  country_name: string;
  phone_length: number;
}

interface Country {
  id: number;
  country: string;
  short_code: string;
  calling_code: number;
  phone_length: number;
  status: number;
  flag: string;
  currency: string;
  currency_symbol: string;
}

interface VisitorData {
  city: string;
  region: string;
  country: string;
  postal: string;
  latitude: string;
  longitude: string;
  org: string;
}

const ContactPage = () => {
  // Country select state
  const [countries, setCountries] = useState<Country[]>([]);
  const [visitorData, setVisitorData] = useState<VisitorData>({
    city: "Unknown",
    region: "Unknown",
    country: "Unknown",
    postal: "Unknown",
    latitude: "Unknown",
    longitude: "Unknown",
    org: "Unknown",
  });
  const [isCountryLoading, setIsCountryLoading] = useState(false);
  const [formFocused, setFormFocused] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "",
    honeypot: "",
    country: "",
    countryCode: "",
    "g-recaptcha-response": "",
  });
  const [formLoading, setFormLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    mobile?: string;
    message?: string;
    captcha?: string;
    country?: string;
    honeypot?: string;
  }>({});
  const recaptchaRef = useRef<any>(null);

  const recaptchaSiteKey = "6Lf9iEYqAAAAAGBkHMfKHyrXoDP3tyX_yZ8cL9b-";
  const assetscountries = getFlagUrl();

  // Fetch countries and set default country based on IP
  useEffect(() => {
    if (!formFocused) return;
    const fetchCountries = async () => {
      setIsCountryLoading(true);
      try {
        const response = await fetch("/api/countries");
        const data = await response.json();
        const list: Country[] = Array.isArray(data) ? data : [];
        setCountries(list);

        // Default immediately so the field never sits blank — the IP lookup
        // below only *upgrades* this if it resolves to a different country.
        const defaultCountry = list.find((c) => c.calling_code === 91);
        if (defaultCountry) {
          setFormData((prev) => ({
            ...prev,
            country: prev.country || defaultCountry.id.toString(),
            countryCode: prev.countryCode || `+${defaultCountry.calling_code}`,
          }));
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      } finally {
        setIsCountryLoading(false);
      }
    };

    fetchCountries();
  }, [formFocused]);

  // Fetch visitor data and set default country
  useEffect(() => {
    if (countries.length > 0) {
      // Server-side lookup (no CORS/rate-limit exposure in the browser — see app/api/geo).
      fetch('/api/geo')
        .then((res) => res.json())
        .then((data) => {
          setVisitorData({
            city: data.city || "Unknown",
            region: data.region || "Unknown",
            country: data.country_code || "Unknown",
            postal: data.postal || "Unknown",
            latitude: data.latitude?.toString() || "Unknown",
            longitude: data.longitude?.toString() || "Unknown",
            org: data.org || "Unknown",
          });

          // Find the country matching the short_code from IP API
          const matchingCountry = countries.find((country) => country.short_code === data.country_code);
          if (matchingCountry) {
            setFormData((prev) => ({
              ...prev,
              country: matchingCountry.id.toString(),
              countryCode: `+${matchingCountry.calling_code}`,
            }));
          } else {
            // Fallback to United States (+1)
            const defaultCountry = countries.find((country) => country.calling_code === 91);
            if (defaultCountry) {
              setFormData((prev) => ({
                ...prev,
                country: defaultCountry.id.toString(),
                countryCode: `+${defaultCountry.calling_code}`,
              }));
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching visitor data:", error);
          setVisitorData({
            city: "Unknown",
            region: "Unknown",
            country: "Unknown",
            postal: "Unknown",
            latitude: "Unknown",
            longitude: "Unknown",
            org: "Unknown",
          });
          // Fallback to United States (+1) on error
          const defaultCountry = countries.find((country) => country.calling_code === 91);
          if (defaultCountry) {
            setFormData((prev) => ({
              ...prev,
              country: defaultCountry.id.toString(),
              countryCode: `+${defaultCountry.calling_code}`,
            }));
          }
        });
    }
  }, [countries]);

  // Country options
  const customOptions: CustomOption[] = countries.map((country) => ({
    value: country.id.toString(),
    label: `+${country.calling_code}`,
    flag: country.flag ? `${assetscountries}${country.flag}` : "",
    calling_code: `+${country.calling_code}`,
    country_name: country.country,
    phone_length: country.phone_length,
  }));

  // Format option label for country dropdown
  const formatOptionLabel = ({ flag, calling_code, country_name }: CustomOption) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {flag && (
        <img
          src={flag}
          alt={`${country_name} flag`}
          style={{ width: "20px" }}
        />
      )}
      <span>{calling_code}</span>
    </div>
  );

  // Custom styles for react-select
  const countryCodeStyles = {
    control: (provided: any) => ({
      ...provided,
      borderRadius: "8px 0 0 8px",
      background: "white",
      fontFamily: "Latinka Regular",
      fontSize: "16px",
      color: "#090D28",
      minWidth: "100px",
      height: "40px",
      border: "2px solid #CEDFFE",
      borderRight: "1px solid #fff",
      outline: "none",
      boxShadow: "none",
      display: "flex",
      alignItems: "center",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#090D28",
      fontFamily: "Latinka Regular",
      fontSize: "16px",
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      display: "none",
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      display: "none",
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: "300px",
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#195AFF",
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        background: "#E6E9FF",
        borderRadius: "3px",
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "#090D28",
      fontFamily: "Latinka Regular",
      fontSize: "16px",
      display: "flex",
      alignItems: "center",
      height: "100%",
    }),
  };

  // Handle form input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    let clean: string;
    if (/email/i.test(name)) {
      clean = value;
    } else if (name === 'mobile') {
      const maxLen = countries.find((c) => c.id.toString() === formData.country)?.phone_length || 10;
      clean = value.replace(/\D/g, '').slice(0, maxLen);
    } else {
      clean = stripDangerousChars(value);
    }
    setFormData((prev) => ({ ...prev, [name]: clean }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Handle country change
  const handleCountryChange = (selectedOption: CustomOption | null) => {
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        country: selectedOption.value,
        countryCode: selectedOption.calling_code,
        mobile: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        country: "",
        countryCode: "",
      }));
    }
  };

  // Handle reCAPTCHA verification
  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    setFormData((prev) => ({ ...prev, "g-recaptcha-response": token || "" }));
    setErrors((prev) => ({ ...prev, captcha: "" }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors: typeof errors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.mobile.trim()) newErrors.mobile = "Phone number is required";
    else if (
      countries.find((c) => c.id.toString() === formData.country)?.phone_length &&
      formData.mobile.length !==
      countries.find((c) => c.id.toString() === formData.country)?.phone_length
    ) {
      newErrors.mobile = `Phone number must be ${countries.find((c) => c.id.toString() === formData.country)?.phone_length
        } digits`;
    }
    if (!formData.message.trim()) newErrors.message = "Message is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email format";
    if (!formData.countryCode) newErrors.country = "Country code is required";
    if (!recaptchaToken) newErrors.captcha = "Please complete the reCAPTCHA";
    if (formData.honeypot.trim()) newErrors.honeypot = "Spam detected";
    return newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      if (validationErrors.captcha) notifyerror(validationErrors.captcha);
      if (validationErrors.honeypot) notifyerror("Spam submission detected. Please try again.");
      return;
    }
    setFormLoading(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      const raw = JSON.stringify({
        form_name: "contact form",
        data: {
          name: formData.name,
          mobile: formData.mobile,
          comment: formData.message,
          email: formData.email,
          countryCode: formData.countryCode,
          "g-recaptcha-response": formData["g-recaptcha-response"],
        },
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow" as RequestRedirect,
      };

      const response = await fetch(getApiUrl("/form-requests"), requestOptions);
      const result = await response.json();
      if (response.status === 200) {
        notify();
        setFormData({
          name: "",
          email: "",
          mobile: "",
          message: "",
          honeypot: "",
          country: formData.country, // Preserve country
          countryCode: formData.countryCode, // Preserve countryCode
          "g-recaptcha-response": "",
        });
        setRecaptchaToken(null);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset(); // Reset reCAPTCHA
        }
        setErrors({});
      } else {
        notifyerror(result?.error || "Form submission failed. Please try again.");
      }
    } catch (error: any) {
      notifyerror(error.message || "Form submission failed. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  const notify = () =>
    toast.success("Form submitted successfully.", {
      style: {
        border: "1px solid #00b852",
        padding: "16px",
        color: "#00b852",
        zIndex: "10000 !important",
      },
      iconTheme: {
        primary: "#00b852",
        secondary: "#FFFAEE",
      },
    });

  const notifyerror = (msg: string) =>
    toast.error(msg, {
      style: {
        border: "1px solid #cc0000",
        padding: "16px",
        color: "#cc0000",
        zIndex: "10000 !important",
      },
      iconTheme: {
        primary: "#cc0000",
        secondary: "#FFFAEE",
      },
    });

  // Function to open VilvaBot chat
  const openVilvaBot = () => {
    let attempts = 0;
    const maxAttempts = 10;
    const interval = setInterval(() => {
      const win = window as Window & { aminos?: any };
      if (win.aminos && typeof win.aminos.openChat === "function") {
        clearInterval(interval);
        win.aminos.openChat();
      } else {
        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          notifyerror("Chat bot is not loaded yet. Please try again in a moment.");
        }
      }
    }, 300);
  };



  return (
    <>
      <div className="py-[20px] md:py-[40px] bg-[radial-gradient(124.01%_100%_at_50%_0%,#015EFF_0%,#01338A_51.07%,#001945_100%)]">
        {/* <p className="text-[#B0FF48] text-center font-LatinkaSemiBold text-[16px] md:text-[18px] font-normal leading-[100%] tracking-[2.88px] uppercase">
          CONTACT US
        </p> */}
        <h1 className="text-white text-center my-[16px] font-NeueMontreal text-[40px] md:text-[56px] font-normal leading-[100%]">
          Let’s Connect & <br className="md:hidden block" /> Grow Together
        </h1>
        <p className="text-[#CADCFC] px-4 text-center schibsted-grotesk text-[16px] md:text-[18px] font-normal leading-[120%]">
          Reach out to us for support, business opportunities, or just to say hello.
        </p>
        <div className="px-4 lg:container mt-[48px]">
          <div className="block md:flex justify-center items-center">
            <div className="w-full md:w-5/12">
              <div className="rounded-[12px] mx-auto md:w-full lg:w-[450px] border-2 border-[#77ADFF] bg-[#D6E5FF] p-[24px]">
                <form onSubmit={handleSubmit} autoComplete="off" onFocus={() => setFormFocused(true)}>
                  <h2 className="text-[#002F81] font-NeueMontreal text-[22px] font-normal leading-[120%] mb-[24px]">
                    Request a Call
                  </h2>
                  {/* Honeypot field (hidden from users) */}
                  <input
                    type="text"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                    style={{ display: "none" }}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                  <div className="mb-4">
                    <label className="text-[#000000] schibsted-grotesk font-medium text-[15px] leading-[100%]">
                      Full name *
                    </label>
                    <input
                      className={`flex h-[40px] px-[16px] items-center gap-[10px] schibsted-grotesk self-stretch rounded-[8px] bg-white w-full border-[2px] ${errors.name ? "border-red-500" : "border-[#CEDFFE]"
                        } focus:outline-none focus:border-[#195AFF]`}
                      type="text"
                      name="name"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>
                  <label className="text-[#000000] schibsted-grotesk font-medium text-[15px] leading-[100%]">
                    Mobile Number *
                  </label>
                  <div className="mb-4 flex items-center">
                    <div className="relative">
                      <Select
                        name="country"
                        instanceId="country-code-select"
                        options={customOptions}
                        value={customOptions.find((option) => option.value === formData.country) || null}
                        onChange={handleCountryChange}
                        formatOptionLabel={formatOptionLabel}
                        styles={countryCodeStyles}
                        placeholder="+91"
                        isDisabled={isCountryLoading}
                        isLoading={isCountryLoading}
                      />
                      <svg
                        className="absolute right-2 top-1/2 -translate-y-1/2 transform pointer-events-none"
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M5 7.5L10 12.5L15 7.5"
                          stroke="black"
                          strokeWidth="1.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <input
                      className={`w-full rounded-[8px] border-l-[#fff] schibsted-grotesk rounded-l-none border-[2px] border-solid h-[40px] px-[16px] py-[10px] items-center gap-[10px] self-stretch bg-white ${errors.mobile ? "border-red-500 border-l-red-500" : "border-[#CEDFFE]"
                        } focus:outline-none focus:border-[#195AFF]`}
                      type="text"
                      name="mobile"
                      placeholder="Enter mobile number"
                      inputMode="numeric"
                      maxLength={countries.find((c) => c.id.toString() === formData.country)?.phone_length || 10}
                      value={formData.mobile}
                      onChange={handleChange}
                      disabled={isCountryLoading}
                    />
                  </div>
                  {(errors.mobile || errors.country) && (
                    <p className="mt-1 text-[12px] text-red-500 schibsted-grotesk">{errors.mobile || errors.country}</p>
                  )}
                  <div className="mb-4">
                    <label className="text-[#000000] schibsted-grotesk font-medium text-[15px] leading-[100%]">
                      Business email *
                    </label>
                    <input
                      className={`flex h-[40px] px-[16px] py-[10px] schibsted-grotesk items-center gap-[10px] self-stretch rounded-[8px] bg-white w-full border-[2px] ${errors.email ? "border-red-500" : "border-[#CEDFFE]"
                        } focus:outline-none focus:border-[#195AFF]`}
                      type="text"
                      name="email"
                      placeholder="Enter your business email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1 schibsted-grotesk">{errors.email}</p>}
                  </div>
                  <div className="mb-4">
                    <label className="text-[#000000] schibsted-grotesk font-medium text-[15px] leading-[100%]">
                      Message *
                    </label>
                    <textarea
                      className={`flex h-[60px] px-[16px] py-[10px] schibsted-grotesk items-center gap-[10px] self-stretch rounded-[8px] bg-white w-full border-[2px] ${errors.message ? "border-red-500" : "border-[#CEDFFE]"
                        } focus:outline-none focus:border-[#195AFF]`}
                      name="message"
                      placeholder="Enter your message"
                      value={formData.message}
                      onChange={handleChange}
                    />
                    {errors.message && <p className="text-red-500 schibsted-grotesk text-xs mt-1">{errors.message}</p>}
                  </div>
                  <div className="mb-4">
                    <div className="relative w-full h-[59px] min-h-[59px] min-[360px]:h-[67px] min-[360px]:min-h-[67px] min-[410px]:h-[78px] min-[410px]:min-h-[78px] overflow-hidden">
                      <div className="absolute top-0 left-0 origin-top-left scale-[0.75] min-[360px]:scale-[0.85] min-[410px]:scale-100">
                        {formFocused ? (
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={recaptchaSiteKey}
                            onChange={handleRecaptchaChange}
                          />
                        ) : (
                          <div
                            className="h-[78px] w-[304px] bg-[#3d3c4e] border border-gray-600 rounded flex items-center justify-center cursor-pointer"
                            onClick={() => setFormFocused(true)}
                          >
                            <span className="text-white schibsted-grotesk text-sm">Click to verify you're human</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {errors.captcha && <p className="text-red-500 schibsted-grotesk text-xs mt-1">{errors.captcha}</p>}
                  </div>
                  <button
                    type="submit"
                    className={`w-full flex items-center justify-center h-[40px] schibsted-grotesk ${formLoading || !recaptchaToken
                      ? "bg-[#C7C7C7] text-[#8A8A8A] cursor-not-allowed"
                      : "bg-[#195AFF] text-white"
                      } rounded-[50px]  font-LatinkaSemiBold text-[18px] mt-2`}
                    disabled={formLoading || !recaptchaToken}
                  >
                    {formLoading ? "Submitting..." : "Submit Message"}
                  </button>
                </form>

              </div>
              <p className="text-[#81AAF0] mx-auto md:w-full lg:w-[450px] md:text-start text-center schibsted-grotesk mt-[14px] text-[13px] font-normal leading-[150%]">
                We value your privacy. By clicking the button above, you agree to receive messages from Vilva
              </p>
            </div>
            {/* This column used to hold only the illustration, so the larger half of the
                screen at the fold carried no information — a visitor who would rather call
                than fill in a form had to scroll past the whole hero to find a number. The
                details below are the same ones already published further down this page, not
                new ones. */}
            <div className="w-full md:w-7/12 mt-[32px] md:mt-0">
              <div className="rounded-[12px] w-full h-full flex flex-col items-center justify-center gap-[24px]">
                <Image
                  src={contactimg}
                  alt="Contact Us"
                  className="rounded-[12px] w-[420px] max-w-full mx-auto h-auto"
                />

                <div className="w-full max-w-[520px] rounded-[12px] border border-white/20 bg-white/10 p-[24px] backdrop-blur-sm">
                  <p className="schibsted-grotesk text-[13px] font-normal uppercase tracking-wider text-[#81AAF0]">
                    Prefer to talk?
                  </p>

                  <a
                    href="tel:+919176569459"
                    className="mt-[8px] block schibsted-grotesk text-[24px] font-[700] leading-[130%] text-white hover:underline"
                  >
                    +91 9176569459
                  </a>

                  <a
                    href="mailto:info@vilvabusiness.com"
                    className="mt-[6px] block schibsted-grotesk text-[16px] font-normal leading-[150%] text-white/90 hover:underline"
                  >
                    info@vilvabusiness.com
                  </a>

                  <p className="mt-[12px] schibsted-grotesk text-[14px] font-normal leading-[150%] text-white/70">
                    Available Mon – Sat, 9:00 AM to 6:30 PM
                  </p>

                  <div className="mt-[20px] flex flex-wrap gap-[12px]">
                    <a
                      href="https://api.whatsapp.com/send?phone=9176569459"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-h-[44px] items-center justify-center gap-[8px] rounded-[8px] bg-[#129F2E] px-[20px] schibsted-grotesk text-[15px] font-normal leading-[120%] text-white transition-colors hover:bg-[#0e8526]"
                    >
                      Chat on WhatsApp
                    </a>
                    <a
                      href="#contact-location"
                      className="inline-flex min-h-[44px] items-center justify-center rounded-[8px] border border-white/40 px-[20px] schibsted-grotesk text-[15px] font-normal leading-[120%] text-white transition-colors hover:bg-white/10"
                    >
                      Find our office
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* id target for the hero's "Find our office" link */}
      <div id="contact-location" className="bg-[#F3F6FC] py-[60px] md:py-[120px] scroll-mt-[100px]">
        <section className="px-4 lg:container">
          <div className="block md:flex gap-[40px] justify-center items-start">
            <div className="w-full md:w-1/2">
              {/* Two offices, so two maps. Previously only Ambattur was shown, under a generic
                  "Vilva Business location" title — a visitor had no way to tell which office it
                  was, and Kodungaiyur was not on the page at all despite being published in the
                  structured data. Each map is now labelled and paired with its own address. */}
              <div>
                <h3 className="mb-[12px] text-[#373844] schibsted-grotesk text-[18px] font-[700] leading-[120%]">
                  Ambattur — Head Office
                </h3>
                <p className="mb-[12px] text-[#717171] schibsted-grotesk text-[14px] font-normal leading-[150%]">
                  3/8, Kalyani Ammal St, Varadaraja Puram, Ambattur, Chennai, Tamil Nadu 600053
                </p>
                <iframe
                  title="Vilva Business Ambattur head office on Google Maps"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3885.79483365388!2d80.14845757462504!3d13.112179987216237!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a52630045335a37%3A0xc1fd164ee1594e02!2sVilva%20Networks!5e0!3m2!1sen!2sin!4v1786614047741!5m2!1sen!2sin"
                  width="100%"
                  height="360"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                ></iframe>
              </div>

              <div className="mt-[32px]">
                <h3 className="mb-[12px] text-[#373844] schibsted-grotesk text-[18px] font-[700] leading-[120%]">
                  Kodungaiyur
                </h3>
                <p className="mb-[12px] text-[#717171] schibsted-grotesk text-[14px] font-normal leading-[150%]">
                  9/11, Narayanaswamy Garden 3rd Main Rd, Kodungaiyur, Chennai, Tamil Nadu 600118
                </p>
                <iframe
                  title="Vilva Business Kodungaiyur office on Google Maps"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3885.2893604954675!2d80.24638477462565!3d13.144138987187342!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a526511af9e5353%3A0x196d22c36e1f9c72!2sVilva%20Networks!5e0!3m2!1sen!2sin!4v1786614873141!5m2!1sen!2sin"
                  width="100%"
                  height="360"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                ></iframe>
              </div>
              {/* Hidden for now, not removed — remove "hidden" to bring VilvaBot back on this page. */}
              <div className="hidden my-[40px] p-[24px] rounded-[12px] border border-[#CEDFFE] bg-white">
                <h3 className="text-[#373844] schibsted-grotesk font-[700] text-[22px]  leading-[120%]">
                  Ask With VilvaBot
                </h3>
                <p className="text-[#373844] schibsted-grotesk text-[16px] font-normal leading-[150%] my-[16px]">
                  Automated. Accurate. Always on VilvaBot handles your business queries anytime.
                </p>
                <button
                  type="button"
                  onClick={openVilvaBot}
                  className="flex w-full md:w-[320px] h-[40px] px-[20px] py-[16px] justify-center items-center gap-[8px] rounded-[8px] bg-[linear-gradient(270deg,#C823FF_0%,#195AFF_100%)] text-white text-[16px] leading-[100%] font-normal schibsted-grotesk"
                >
                  <SvgIcons.ChantBotContact /> Chat with VilvaBot
                </button>
              </div>
            </div>
            <div className="w-full md:w-1/2">
              <div className="p-[24px] mb-[40px] rounded-[12px] border border-[#CEDFFE] bg-white">
                <h4 className="text-[#373844] schibsted-grotesk font-[700] text-[22px] not-italic leading-[120%]">
                  Visit Us
                </h4>
                {/* Head office first, and both labelled. The two addresses used to sit here
                    unlabelled with Kodungaiyur on top, so a visitor could not tell which was the
                    main office — and the order contradicted the footer and the maps below. */}
                <p className="pt-[24px] text-[#717171] schibsted-grotesk text-[13px] font-semibold uppercase tracking-wider leading-[150%]">
                  Ambattur — Head Office
                </p>
                <p className="text-[#373844] schibsted-grotesk pb-[16px] text-[18px] font-normal leading-[150%]">
                  3/8, Kalyani Ammal St, Varadaraja Puram, Ambattur, Chennai - 600053, Tamil Nadu, India.
                </p>
                <p className="text-[#717171] schibsted-grotesk text-[13px] font-semibold uppercase tracking-wider leading-[150%]">
                  Kodungaiyur
                </p>
                <p className="text-[#373844] schibsted-grotesk text-[18px] pb-[24px] font-normal leading-[150%]">
                  9/11 Narayanaswamy Garden, 3rd Main Road, Kodungaiyur, Chennai – 600118, Tamilnadu, India.
                </p>
                <section className="flex items-center justify-start gap-3">
                  <TestiReviewCommonImages />
                  <div>
                    <a
                      href="https://maps.app.goo.gl/wxZr7ckDhrWnVbx27"
                      className="text-[#090D28]"
                      target="_blank"
                    >
                      <p className="m-0 flex items-center gap-1 p-0 pb-[6px] text-center schibsted-grotesk font-medium text-sm font-normal leading-[120%] text-[#000]">
                        <Image
                          className="w-6"
                          src={getImageUrl("/vilvabusiness/website/common/homepage/google-img.webp")}
                          width={50}
                          height={50}
                          alt="GoogleImg"
                        />{" "}
                        Google Reviews
                      </p>
                    </a>
                    <div className="flex items-center gap-2">
                      4.7
                      <div className="flex items-center gap-2">
                        <SvgIcons.ReviewStar />
                        <SvgIcons.ReviewStar />
                        <SvgIcons.ReviewStar />
                        <SvgIcons.ReviewStar />
                        <SvgIcons.ReviewStar />
                      </div>
                    </div>
                  </div>
                </section>
              </div>
              <div className="p-[24px] mb-[40px] rounded-[12px] border border-[#CEDFFE] bg-white">
                <h4 className="text-[#373844] schibsted-grotesk font-[700] text-[22px] not-italic leading-[120%]">
                  Talk to Our Team
                </h4>
                <p className="text-[#373844] my-[16px] schibsted-grotesk text-[16px] font-normal leading-[150%]">
                  Have questions? Give us a call at{" "}
                  <a
                    className="text-[#195AFF] schibsted-grotesk text-[20px] !underline font-normal leading-[150%] whitespace-nowrap"
                    href="tel:+91 9176569459"
                  >
                    +91 9176569459
                  </a>
                </p>
                <p className="text-[#373844] mb-[16px] schibsted-grotesk text-[16px] font-normal leading-[150%]">
                  Email at{" "}
                  <a
                    className="text-[#195AFF] schibsted-grotesk text-[20px] font-normal leading-[150%] !underline"
                    href="mailto:info@vilvabusiness.com"
                  >
                    info@vilvabusiness.com
                  </a>
                </p>
                <p className="text-[#717171] schibsted-grotesk text-[14px] font-normal leading-[150%]">
                  Available Mon - Sat, 9:00 AM to 6:30 PM.
                </p>
              </div>
              <div className="p-[24px] rounded-[12px] border border-[#CEDFFE] bg-white">
                <h3 className="text-[#373844] schibsted-grotesk font-[700] text-[22px] leading-[120%]">
                  Connect on Whatsapp
                </h3>
                <p className="text-[#373844] schibsted-grotesk text-[16px] font-normal leading-[150%] my-[16px]">
                  Want to know more? Start a WhatsApp chat with us now!
                </p>
                <a
                  href="https://api.whatsapp.com/send?phone=9176569459"
                  target="_blank"
                  className="flex w-full md:w-[220px] h-[40px] px-[20px] py-[16px] justify-center items-center gap-[8px] rounded-[8px] bg-[#129F2E] text-white text-[16px] leading-[120%] font-normal schibsted-grotesk"
                >
                  <SvgIcons.ContactWhatsapp /> Chat on Whatsapp
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
      <section>
        {/* <h2 className="text-[#090D28] text-center mt-[60px] md:mt-[120px] text-[32px] w-full mx-auto whitespace-normal md:whitespace-nowrap md:text-[40px] font-extrabold leading-[120%] schibsted-grotesk">
          Your questions Our Answers
        </h2> */}
        <FaqSection faqs={contactFaqs} />
      </section>
    </>
  );
};

export default ContactPage;